import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";

export default function ReviewsSection({ companyId, onAggregateChange }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () =>
    api.get(`/removal-companies/${companyId}/reviews`).then(({ data }) => setItems(data.items || []));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  const submit = async () => {
    if (!user) { toast.error("Please log in to leave a review"); return; }
    if (rating < 1) { toast.error("Pick a star rating"); return; }
    setSubmitting(true);
    try {
      await api.post(`/removal-companies/${companyId}/reviews`, { rating, comment });
      toast.success("Review submitted");
      setComment(""); setRating(0);
      await load();
      if (onAggregateChange) onAggregateChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="border border-border bg-white p-8">
      <div className="overline text-safety">Reviews</div>
      <div className="mt-2 font-display text-2xl tracking-tight text-forest">
        {items.length} review{items.length === 1 ? "" : "s"}
      </div>

      {user && (
        <div className="mt-6 border-t border-border pt-6">
          <div className="overline text-muted-foreground mb-3">Leave a review</div>
          <div className="flex items-center gap-1 mb-3">
            {[1,2,3,4,5].map((n) => (
              <button
                key={n}
                type="button"
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setRating(n)}
                data-testid={`review-star-${n}`}
                className="p-1"
                aria-label={`${n} stars`}
              >
                <Star className={`w-6 h-6 ${(hover || rating) >= n ? "fill-safety text-safety" : "text-muted-foreground"}`} />
              </button>
            ))}
          </div>
          <Textarea
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="How was your experience?"
            data-testid="review-comment"
            className="rounded-none"
          />
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="review-submit"
            className="mt-3 rounded-none bg-forest hover:bg-forest-800 text-white h-11 px-6"
          >
            {submitting ? "Posting…" : "Post review"}
          </Button>
        </div>
      )}

      <div className="mt-6 space-y-4">
        {items.length === 0 && <div className="text-sm text-muted-foreground">No reviews yet — be the first.</div>}
        {items.map((r) => (
          <div key={r.review_id} data-testid={`review-item-${r.review_id}`} className="border-t border-border pt-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map((n) => (
                  <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-safety text-safety" : "text-muted-foreground"}`} />
                ))}
              </div>
              <div className="font-display text-sm">{r.user_name}</div>
              <div className="overline text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString("en-GB")}</div>
            </div>
            {r.comment && <p className="mt-2 text-sm text-foreground/90 leading-relaxed">{r.comment}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
